import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type AITaskMode = 'single' | 'consensus' | 'fact_check'
export type FactcheckLeniency = 'strict' | 'permissive'

/**
 * Hydrated task-config row, as returned by GET /admin/tasks (list) and used
 * by UI surfaces. Provider legs carry `providerName` for display; the write
 * payload uses `providerId` (see UpdateAITaskConfigInput).
 */
export interface AITaskConfig {
  taskName: string
  mode: AITaskMode
  primary: { providerName: string; model: string }
  secondary?: { providerName: string; model: string }
  fallback?: { providerName: string; model: string }
  promptTemplate: string | null
  confidenceGateThreshold?: number
  factcheckLeniency?: FactcheckLeniency
  overridden: boolean
  updatedAt?: string
}

export interface ListTaskConfigsResponse {
  tasks: AITaskConfig[]
}

/**
 * GET /admin/tasks/:task returns the raw DB row (or null) plus a `codeDefault`
 * projection. The exact row shape is owned server-side; hooks expose it as
 * `unknown`-compatible JSON so the UI can render a read-only detail panel
 * without pretending to know the column names.
 */
export interface TaskConfigDetail {
  current: Record<string, unknown> | null
  codeDefault: Record<string, unknown> | null
}

/** Write payload for PUT /admin/tasks/:task — provider legs reference uuid. */
export interface TaskProviderLeg {
  providerId: string
  model: string
}

export interface UpdateAITaskConfigInput {
  mode: AITaskMode
  primary: TaskProviderLeg
  secondary?: TaskProviderLeg
  fallback?: TaskProviderLeg
  promptTemplate?: string | null
  confidenceGateThreshold?: number
  factcheckLeniency?: FactcheckLeniency
}

export interface TestTaskOnSampleResponse {
  ok: boolean
  output?: unknown
  error?: string
}

/** List every AI task config — DB overrides plus code-only defaults. */
export function useAITaskConfigs() {
  return useQuery({
    queryKey: ['ai-task-configs'],
    queryFn: () => api.get<ListTaskConfigsResponse>('/admin/tasks'),
    staleTime: 10_000,
  })
}

/** Single task config with code-default projection. */
export function useAITaskConfig(taskName: string | undefined) {
  return useQuery({
    queryKey: ['ai-task-configs', taskName],
    queryFn: () => api.get<TaskConfigDetail>(`/admin/tasks/${encodeURIComponent(taskName!)}`),
    enabled: Boolean(taskName),
  })
}

/** Upsert (override) a task config. */
export function useUpdateAITaskConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskName, ...body }: { taskName: string } & UpdateAITaskConfigInput) =>
      api.put<{ ok: true }>(`/admin/tasks/${encodeURIComponent(taskName)}`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['ai-task-configs'] })
      qc.invalidateQueries({ queryKey: ['ai-task-configs', vars.taskName] })
    },
  })
}

/** Reset a task config to its code default by deleting the DB override. */
export function useResetAITaskConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (taskName: string) =>
      api.delete<{ ok: true }>(`/admin/tasks/${encodeURIComponent(taskName)}/overrides`),
    onSuccess: (_data, taskName) => {
      qc.invalidateQueries({ queryKey: ['ai-task-configs'] })
      qc.invalidateQueries({ queryKey: ['ai-task-configs', taskName] })
    },
  })
}

/** Dry-run a task against a sample input — routes through the live AIRouter. */
export function useTestAITaskOnSample() {
  return useMutation({
    mutationFn: ({ taskName, sampleInput }: { taskName: string; sampleInput: string }) =>
      api.post<TestTaskOnSampleResponse>(
        `/admin/tasks/${encodeURIComponent(taskName)}/test`,
        { sampleInput },
      ),
  })
}
